import { DataModelBuilder, SerializationContext, SerializedData, isSameData } from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { ApplicationFeeProtocol } from './application-fee-protocol.js';
import { Fee } from '../fee.js';
import { Price } from '../price.js';
import { VersionRange } from '../../core/version-range.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';
import { fromEnumAsStringOrNull, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';

export class StaticApplicationFeeProtocol extends ApplicationFeeProtocol {
  static readonly TYPE_ID: string = '5b39e4d6fae643dda63068283a31ab27';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly fee: Fee,
    public readonly maxFee: Price | null,
    public readonly overrideVat: InvoiceVat | null,
  ) {
    super();
  }

  get $typeId(): string {
    return StaticApplicationFeeProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, StaticApplicationFeeProtocol.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): StaticApplicationFeeProtocol {
    return new StaticApplicationFeeProtocol(
        context.fromData(MapUtils.get<SerializedData>(map, 'fee')),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'maxFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'fee': context.toData(this.fee),
      'maxFee': this.maxFee?.encode() ?? null,
      'overrideVat': fromEnumAsStringOrNull(this.overrideVat),
    };
  }

  clone(): this {
    return new StaticApplicationFeeProtocol(
        this.fee.clone(),
        this.maxFee?.clone() ?? null,
        this.overrideVat,
    ) as this;
  }

  isSame(other: StaticApplicationFeeProtocol): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.fee, this.fee) &&
        isSameData(other.maxFee, this.maxFee) &&
        other.overrideVat == this.overrideVat;
  }

  getUnitFee(unitPrice: Price): Fee | null {
    return this.fee.clone();
  }

  calculateUnitFee(unitPrice: Price): Price {
    let unitFee = this.fee.calculateUnitFee(unitPrice);
    if(this.maxFee != null && this.maxFee.value > 0 && unitFee.compareTo(this.maxFee) > 0) {
      unitFee = this.maxFee.clone();
    }

    return unitFee;
  }
}

export class StaticApplicationFeeProtocolBuilder extends DataModelBuilder<StaticApplicationFeeProtocol> {
  build(context: SerializationContext, map: SerializedData): StaticApplicationFeeProtocol {
    return StaticApplicationFeeProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(StaticApplicationFeeProtocol.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(StaticApplicationFeeProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `maxFee` of type `Price | null` was introduced.
    if(version < 2) {
      version = 2;
    }

    // In this version parameter `overrideVat` of type `InvoiceVat | null` was introduced.
    if(version < 3) {
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
